import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';

import { store, AppDispatch } from './store';
import { checkAuth, fetchCurrentUser } from './store/slices/authSlice';
import PrivateRoute from './components/auth/PrivateRoute';
import ServiceRoute from './components/auth/ServiceRoute';
import RequireAgreements from './components/auth/RequireAgreements';
import SmartRedirect from './components/auth/SmartRedirect';
import SecureDashboardRouter from './components/auth/SecureDashboardRouter';
import AppLayout from './layouts/AppLayout';
import ServiceLayout from './layouts/ServiceLayout';
import { useTokenRefresh } from './hooks/useTokenRefresh';
import { getAccessToken } from './utils/auth';
import { UnifiedAuthProvider } from './contexts/UnifiedAuthContext';
import './i18n';

// Pages
import RegisterPage from './pages/auth/RegisterPage';
import PilotRegistrationPageV2 from './pages/auth/PilotRegistrationPageV2';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import PilotDashboard from './pages/pilot/PilotDashboard';
import EmailVerificationPage from './pages/auth/EmailVerificationPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import CardsPage from './pages/cards/CardsPage';
import CardDetailsPage from './pages/cards/CardDetailsPage';
import ImportCardsPage from './pages/cards/ImportCardsPage';
import SearchCardsPage from './pages/cards/SearchCardsPage';
import SendEmailPage from './pages/cards/SendEmailPage';
import ClubsPage from './pages/admin/ClubsPage';
import UsersPage from './pages/admin/UsersPage';
import ActivitiesPage from './pages/admin/ActivitiesPage';
import CreateUser from './pages/admin/CreateUser';
import CreateClub from './pages/admin/CreateClub';
import ConfigureClub from './pages/admin/ConfigureClub';
import CreateMember from './pages/admin/CreateMember';
import PlatformNewsManagementPage from './pages/admin/PlatformNewsManagementPage';
import ProfilePage from './pages/auth/ProfilePage';
import ApiTokenPage from './pages/auth/ApiTokenPage';
import RegistrationFormPage from './pages/public/RegistrationFormPage';
import ShuttleManagementPage from './pages/admin/ShuttleManagementPageV2';
import ShuttleReports from './components/ShuttleReports';
import InsuranceFormPage from './pages/public/InsuranceFormPage';
import LandingPage from './pages/public/LandingPage';
import VisitorCardsPage from './pages/visitors/VisitorCardsPage';
import ReleaseVisitorCardPage from './pages/visitors/ReleaseVisitorCardPage';
import RemindersAdminPage from './pages/admin/RemindersAdminPage';
import AddEquipmentPage from './pages/reminders/AddEquipmentPage';
import EditEquipmentPage from './pages/reminders/EditEquipmentPage';
import ScheduleMaintenancePage from './pages/reminders/ScheduleMaintenancePage';
import EquipmentDashboard from './pages/reminders/EquipmentDashboard';
import VerifyCardPage from './pages/public/VerifyCardPage';
import PublicCardViewPage from './pages/public/PublicCardViewPage';
import ServicesPortal from './pages/services/ServicesPortal';
import MarketplaceServicePage from './pages/marketplace/MarketplaceServicePage';
import MarketplaceDashboard from './pages/marketplace/MarketplaceDashboard';
import ShuttleServicePage from './pages/shuttle/ShuttleServicePage';
import ListingDetailPage from './pages/marketplace/ListingDetailPage';
import CreateListingPage from './pages/marketplace/CreateListingPage';
import MyListingsPage from './pages/marketplace/MyListingsPage';
import EditListingPage from './pages/marketplace/EditListingPage';
import LogbookDashboard from './pages/logbook/LogbookDashboard';
import FlightForm from './pages/logbook/FlightForm';
import YearlyReportPage from './pages/logbook/YearlyReport';
import ShuttleRegistrationPage from './pages/public/ShuttleRegistrationPage';
import ShuttlePassengersPage from './pages/public/ShuttlePassengersPage';
import ShuttleBookingDetailsPage from './pages/public/ShuttleBookingDetailsPage';
import DriverViewPage from './pages/public/DriverViewPage';
import DriverLandingPage from './pages/public/DriverLandingPage';
import ServiceAddendumPage from './pages/public/ServiceAddendumPage';
import ClubAgreementPage from './pages/public/ClubAgreementPage';

const theme = createTheme({
  palette: {
    primary: {
      main: '#3388ff',
      light: '#66a3ff',
      dark: '#0066ff',
    },
    secondary: {
      main: '#ff00fb',
      light: '#ff66fc',
      dark: '#cc00c8',
    },
    error: {
      main: '#ff4444',
    },
    success: {
      main: '#00c853',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '8px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          },
        },
        contained: {
          background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)',
          '&:hover': {
            background: 'linear-gradient(45deg, #0066ff 30%, #3388ff 90%)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
          '&:hover': {
            boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          transition: 'all 0.3s cubic-bezier(.25,.8,.25,1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 14px 28px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
          },
        },
      },
    },
  },
});


function AppContent() {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    // console.log('[App] Initial mount, current path:', window.location.pathname);
    // Check authentication state
    dispatch(checkAuth());
    
    // If we have a platform token, fetch current user details
    const token = getAccessToken();
    if (token) {
      // console.log('[App] Token found, fetching current user');
      dispatch(fetchCurrentUser());
    }
  }, [dispatch]);

  // Set up automatic token refresh
  useTokenRefresh();

  return (
    <UnifiedAuthProvider>
      <Router>
        <Routes>
        {/* Public routes with layout */}
        <Route element={<AppLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/registrations" element={<PilotRegistrationPageV2 />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email/:userId" element={<EmailVerificationPage />} />
          <Route path="/register/:clubId" element={<RegistrationFormPage />} />
          <Route path="/insurance" element={<InsuranceFormPage />} />
          <Route path="/insurance/:code" element={<InsuranceFormPage />} />
          <Route path="/verify/:cardId" element={<VerifyCardPage />} />
          <Route path="/card/:cardId" element={<PublicCardViewPage />} />
          <Route path="/reminders/equipment/add" element={<AddEquipmentPage />} />
          <Route path="/reminders/equipment/edit/:id" element={<EditEquipmentPage />} />
          <Route path="/reminders/maintenance/schedule/:equipmentId" element={<ScheduleMaintenancePage />} />
          <Route path="/shuttle-registration" element={<ShuttleRegistrationPage />} />
          <Route path="/shuttle-booking/:bookingCode" element={<ShuttleBookingDetailsPage />} />
          <Route path="/shuttle/:scheduleId/passengers" element={<ShuttlePassengersPage />} />
          <Route path="/shuttle/:scheduleId/driver" element={<DriverViewPage />} />
          <Route path="/driver" element={<DriverLandingPage />} />
          <Route path="/service-addendum" element={<ServiceAddendumPage />} />
          <Route path="/club-agreement" element={<ClubAgreementPage />} />
        </Route>


        {/* Redirect routes */}
        <Route path="/pilot-dashboard" element={<Navigate to="/app/pilot-dashboard" replace />} />
        <Route path="/shuttle-booking" element={<Navigate to="/app/shuttle-booking" replace />} />

        {/* Protected routes - all using AppLayout (with AppHeader) */}
        <Route
          path="/app"
          element={
            <PrivateRoute>
              <RequireAgreements>
                <AppLayout />
              </RequireAgreements>
            </PrivateRoute>
          }
        >
          <Route index element={<SmartRedirect />} />
          <Route 
            path="dashboard" 
            element={
              <SecureDashboardRouter requiredDashboardType="club_manager">
                <DashboardPage />
              </SecureDashboardRouter>
            } 
          />
          <Route 
            path="pilot-dashboard" 
            element={
              <SecureDashboardRouter requiredDashboardType="pilot">
                <PilotDashboard />
              </SecureDashboardRouter>
            } 
          />
          <Route path="cards" element={<CardsPage />} />
          <Route path="cards/search" element={<SearchCardsPage />} />
          <Route path="cards/:cardId" element={<CardDetailsPage />} />
          <Route path="cards/import" element={<ImportCardsPage />} />
          <Route path="cards/send-email" element={<SendEmailPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="api-token" element={<ApiTokenPage />} />
          
          {/* Admin routes */}
          <Route path="admin/create-user" element={<CreateUser />} />
          <Route path="admin/create-club" element={<CreateClub />} />
          <Route path="admin/configure-club/:clubId" element={<ConfigureClub />} />
          <Route path="admin/create-member" element={<CreateMember />} />
          <Route path="admin/clubs" element={<ClubsPage />} />
          <Route path="admin/users" element={<UsersPage />} />
          <Route path="admin/activities" element={<ActivitiesPage />} />
          <Route path="admin/platform-news" element={<PlatformNewsManagementPage />} />
          <Route path="admin/reminders" element={<RemindersAdminPage />} />
          
          {/* Visitor routes */}
          <Route path="visitors" element={<VisitorCardsPage />} />
          <Route path="visitors/release" element={<ReleaseVisitorCardPage />} />
          
          {/* Shuttle routes */}
          <Route path="shuttles" element={<ShuttleManagementPage />} />
          <Route path="shuttle-reports" element={<ShuttleReports />} />

          {/* Logbook routes */}
          <Route
            path="logbook"
            element={
              <SecureDashboardRouter requiredDashboardType="pilot">
                <LogbookDashboard />
              </SecureDashboardRouter>
            }
          />
          <Route
            path="logbook/add"
            element={
              <SecureDashboardRouter requiredDashboardType="pilot">
                <FlightForm />
              </SecureDashboardRouter>
            }
          />
          <Route
            path="logbook/edit/:flightId"
            element={
              <SecureDashboardRouter requiredDashboardType="pilot">
                <FlightForm />
              </SecureDashboardRouter>
            }
          />
          <Route
            path="logbook/report"
            element={
              <SecureDashboardRouter requiredDashboardType="pilot">
                <YearlyReportPage />
              </SecureDashboardRouter>
            }
          />
        </Route>

        {/* Service routes - using ServiceRoute wrapper for mixed auth */}
        <Route
          path="/app"
          element={
            <ServiceRoute>
              <AppLayout />
            </ServiceRoute>
          }
        >
          <Route path="shuttle-booking" element={<ShuttleServicePage />} />
          <Route path="reminders/dashboard" element={<EquipmentDashboard />} />
          <Route path="reminders/equipment/add" element={<AddEquipmentPage />} />
          <Route path="reminders/equipment/edit/:id" element={<EditEquipmentPage />} />
          <Route path="reminders/maintenance/schedule/:equipmentId" element={<ScheduleMaintenancePage />} />
          <Route path="services" element={<ServicesPortal />} />
          <Route path="marketplace" element={<MarketplaceServicePage />} />
          <Route path="marketplace/dashboard" element={<MarketplaceDashboard />} />
          <Route path="marketplace/listing/:id" element={<ListingDetailPage />} />
          <Route path="marketplace/create" element={<CreateListingPage />} />
          <Route path="marketplace/my-listings" element={<MyListingsPage />} />
          <Route path="marketplace/edit/:id" element={<EditListingPage />} />
        </Route>
      </Routes>
    </Router>
    </UnifiedAuthProvider>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <AppContent />
        </LocalizationProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
